// SeoLandingPage.js - Phase MM-5
//
// Renders a programmatic SEO landing page from
// public/seo-content/{intent}/{slug}.md. Frontmatter (YAML-ish) at the top
// drives: <title>, <meta description>, <link rel=canonical>, OG tags, and
// JSON-LD structured data injected into <head>. Body is markdown.
//
// One component handles all four intent routes: /build/:slug, /automate/:slug,
// /make/:slug, /recipe/:slug. The intent comes from the URL.

import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import EmailCapture from './EmailCapture';
import './App.css';

const INDEX_URL = '/seo-content/_index.json';
const CONTENT_BASE = '/seo-content';

// Minimal YAML-ish front-matter parser: handles flat keys, indented two-level
// nesting (ctaPrimary / ctaSecondary / faq), and inline arrays + strings. Not
// a full YAML implementation, but covers everything the SEO pipeline emits.
function parseFrontmatter(raw) {
  if (!raw.startsWith('---')) return { frontmatter: {}, body: raw };
  const end = raw.indexOf('\n---', 3);
  if (end < 0) return { frontmatter: {}, body: raw };
  const headBlock = raw.slice(3, end).trim();
  const body = raw.slice(end + 4).replace(/^\s*\n/, '');

  const fm = {};
  const lines = headBlock.split(/\r?\n/);
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) { i++; continue; }
    const m = line.match(/^([A-Za-z][\w-]*)\s*:\s*(.*)$/);
    if (!m) { i++; continue; }
    const [, key, rest] = m;
    const trimmed = rest.trim();

    if (trimmed === '') {
      // Nested object or list of objects on subsequent indented lines
      const sub = [];
      let j = i + 1;
      while (j < lines.length && /^\s+/.test(lines[j])) {
        sub.push(lines[j]);
        j++;
      }
      i = j;

      if (sub.length === 0) {
        fm[key] = '';
        continue;
      }
      // List of objects? (lines starting with '- ')
      if (sub.some((s) => s.trim().startsWith('- '))) {
        const items = [];
        let current = null;
        for (const s of sub) {
          const t = s.trim();
          if (t.startsWith('- ')) {
            if (current) items.push(current);
            current = {};
            const after = t.slice(2);
            const sub2 = after.match(/^([A-Za-z][\w-]*)\s*:\s*(.*)$/);
            if (sub2) {
              current[sub2[1]] = unquote(sub2[2]);
            }
          } else {
            const sub2 = t.match(/^([A-Za-z][\w-]*)\s*:\s*(.*)$/);
            if (sub2 && current) {
              current[sub2[1]] = unquote(sub2[2]);
            }
          }
        }
        if (current) items.push(current);
        fm[key] = items;
      } else {
        // Flat nested object
        const obj = {};
        for (const s of sub) {
          const sub2 = s.trim().match(/^([A-Za-z][\w-]*)\s*:\s*(.*)$/);
          if (sub2) obj[sub2[1]] = unquote(sub2[2]);
        }
        fm[key] = obj;
      }
    } else if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
      const inner = trimmed.slice(1, -1).trim();
      if (!inner) fm[key] = [];
      else fm[key] = inner.split(',').map((s) => unquote(s.trim()));
      i++;
    } else {
      fm[key] = unquote(trimmed);
      i++;
    }
  }
  return { frontmatter: fm, body };
}

function unquote(s) {
  if (typeof s !== 'string') return s;
  const t = s.trim();
  if ((t.startsWith("'") && t.endsWith("'")) || (t.startsWith('"') && t.endsWith('"'))) {
    return t.slice(1, -1);
  }
  return t;
}

const INTENT_LABEL = {
  build: 'Build',
  automate: 'Automate',
  make: 'Make',
  recipe: 'Recipe',
};

// Wrap a single intent so the component can also reuse the constant route
// definitions if we later want to render category indexes.
const SeoLandingPage = ({ intent }) => {
  const params = useParams();
  const resolvedIntent = intent || params.intent || 'build';
  const slug = params.slug;

  // Entry is set but never read — the render uses `frontmatter` + `body`.
  const [, setIndexEntry] = useState(null);
  const [body, setBody] = useState(null);
  const [frontmatter, setFrontmatter] = useState({});
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    let cancelled = false;
    (async () => {
      try {
        const idxRes = await fetch(INDEX_URL, { cache: 'no-cache' });
        if (!idxRes.ok) throw new Error(`Index fetch failed (${idxRes.status}).`);
        const idx = await idxRes.json();
        if (cancelled) return;
        const entry = (idx.pages || []).find(
          (p) => p.slug === slug && p.intent === resolvedIntent
        );
        if (!entry) {
          throw new Error('Page not found.');
        }
        setIndexEntry(entry);

        const fileURL = `${CONTENT_BASE}/${resolvedIntent}/${entry.file || (entry.slug + '.md')}`;
        const r = await fetch(fileURL, { cache: 'no-cache' });
        if (!r.ok) throw new Error(`Could not load page (${r.status}).`);
        const raw = await r.text();
        const { frontmatter: fm, body: md } = parseFrontmatter(raw);
        if (!cancelled) {
          setFrontmatter(fm);
          setBody(md);
        }
      } catch (err) {
        if (!cancelled) setError(err.message || String(err));
      }
    })();
    return () => { cancelled = true; };
  }, [resolvedIntent, slug]);

  // Document head: title, meta description, canonical, OG tags, JSON-LD.
  useEffect(() => {
    if (!frontmatter || !frontmatter.title) return;
    const previousTitle = document.title;
    document.title = frontmatter.title + ' - mChatAI';

    const tags = [];
    const upsertMeta = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
        document.head.appendChild(el);
        tags.push(el);
      } else {
        for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
      }
    };
    if (frontmatter.metaDescription) {
      upsertMeta('meta[name="description"]', {
        name: 'description',
        content: String(frontmatter.metaDescription),
      });
    }
    if (frontmatter.ogTitle) {
      upsertMeta('meta[property="og:title"]', {
        property: 'og:title',
        content: String(frontmatter.ogTitle),
      });
    }
    if (frontmatter.ogDescription) {
      upsertMeta('meta[property="og:description"]', {
        property: 'og:description',
        content: String(frontmatter.ogDescription),
      });
    }
    upsertMeta('meta[property="og:type"]', { property: 'og:type', content: 'article' });
    if (frontmatter.canonical) {
      upsertMeta('meta[property="og:url"]', {
        property: 'og:url',
        content: String(frontmatter.canonical),
      });
    }

    // <link rel="canonical">
    let canonical = document.head.querySelector('link[rel="canonical"][data-mchat-seo="true"]');
    if (frontmatter.canonical) {
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        canonical.setAttribute('data-mchat-seo', 'true');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', String(frontmatter.canonical));
    }

    // JSON-LD structured data
    const jsonLd = buildJsonLd(frontmatter);
    const scriptId = `mchat-jsonld-${slug}`;
    let scriptTag = document.getElementById(scriptId);
    if (jsonLd) {
      if (!scriptTag) {
        scriptTag = document.createElement('script');
        scriptTag.id = scriptId;
        scriptTag.type = 'application/ld+json';
        document.head.appendChild(scriptTag);
      }
      scriptTag.textContent = JSON.stringify(jsonLd);
    }

    return () => {
      document.title = previousTitle;
      for (const t of tags) {
        if (t.parentNode) t.parentNode.removeChild(t);
      }
      if (canonical && canonical.parentNode) canonical.parentNode.removeChild(canonical);
      const s = document.getElementById(scriptId);
      if (s && s.parentNode) s.parentNode.removeChild(s);
    };
  }, [frontmatter, slug]);

  const markdownComponents = useMemo(() => ({
    h1: ({ children }) => (
      <h1 style={{ fontSize: '2.4rem', fontWeight: 800, color: '#111827', margin: '0 0 24px', lineHeight: 1.15 }}>{children}</h1>
    ),
    h2: ({ children }) => (
      <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827', margin: '40px 0 12px' }}>{children}</h2>
    ),
    h3: ({ children }) => (
      <h3 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#111827', margin: '28px 0 8px' }}>{children}</h3>
    ),
    p: ({ children }) => (
      <p style={{ color: '#374151', lineHeight: 1.75, marginBottom: 16, fontSize: '1.04rem' }}>{children}</p>
    ),
    ul: ({ children }) => (
      <ul style={{ color: '#374151', lineHeight: 1.8, marginBottom: 16, paddingLeft: 24, fontSize: '1.04rem' }}>{children}</ul>
    ),
    ol: ({ children }) => (
      <ol style={{ color: '#374151', lineHeight: 1.8, marginBottom: 16, paddingLeft: 24, fontSize: '1.04rem' }}>{children}</ol>
    ),
    a: ({ href, children, ...rest }) => (
      <a href={href} style={{ color: '#3b82f6', textDecoration: 'underline' }} {...rest}>{children}</a>
    ),
  }), []);

  return (
    <div className="mp-page" style={{ background: '#fff', minHeight: 'calc(100vh - 60px)' }}>
      <article style={{ maxWidth: 720, margin: '0 auto', padding: '64px 24px 32px' }}>
        <div style={{ marginBottom: 12, fontSize: '0.85rem', color: '#6b7280' }}>
          <Link to="/hub" style={{ color: '#3b82f6', textDecoration: 'none' }}>Hub</Link>
          <span style={{ margin: '0 8px', color: '#9ca3af' }}>{'>'}</span>
          <span>{INTENT_LABEL[resolvedIntent] || resolvedIntent}</span>
        </div>

        {error && (
          <div style={{
            background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b',
            padding: 16, borderRadius: 8, marginBottom: 24
          }}>
            {error}
          </div>
        )}

        {!body && !error && (
          <p style={{ color: '#6b7280' }}>Loading...</p>
        )}

        {body && (
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
            {body}
          </ReactMarkdown>
        )}

        {/* CTA card */}
        {frontmatter.ctaPrimary && (
          <div style={{
            marginTop: 40,
            padding: 24,
            border: '1px solid #e5e7eb',
            borderRadius: 12,
            background: '#f9fafb',
            textAlign: 'center'
          }}>
            <a
              href={frontmatter.ctaPrimary.url || 'https://mchatai.com/macos-app'}
              style={{
                display: 'inline-block',
                padding: '12px 28px',
                background: '#3b82f6',
                color: '#fff',
                textDecoration: 'none',
                borderRadius: 8,
                fontSize: '1rem',
                fontWeight: 600,
                marginBottom: 8
              }}
            >
              {frontmatter.ctaPrimary.label || 'Get mChatAI'}
            </a>
            {frontmatter.ctaSecondary && (
              <div>
                <a
                  href={frontmatter.ctaSecondary.url || 'https://mchatai.com/hub'}
                  style={{
                    fontSize: '0.95rem',
                    color: '#6b7280',
                    textDecoration: 'none',
                    marginTop: 4
                  }}
                >
                  {frontmatter.ctaSecondary.label || 'Browse the Hub'}
                </a>
              </div>
            )}
          </div>
        )}

        {/* Related recipes rail */}
        {Array.isArray(frontmatter.relatedRecipes) && frontmatter.relatedRecipes.length > 0 && (
          <div style={{ marginTop: 32 }}>
            <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 12px' }}>
              Related recipes
            </h3>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {frontmatter.relatedRecipes.map((slugRef) => (
                <Link
                  key={slugRef}
                  to={`/recipe/${slugRef}`}
                  style={{
                    fontSize: '0.85rem',
                    color: '#3b82f6',
                    textDecoration: 'none',
                    background: '#eff6ff',
                    padding: '6px 12px',
                    borderRadius: 999
                  }}
                >
                  {slugRef.replace(/-/g, ' ')}
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>

      <section style={{ maxWidth: 720, margin: '0 auto', padding: '32px 24px 96px' }}>
        <EmailCapture
          source={`seo:${resolvedIntent}:${slug}`}
          title="Want more guides like this?"
          subtitle="Get one new mChatAI recipe + behind-the-scenes note each Sunday."
        />
      </section>
    </div>
  );
};

// Build a JSON-LD object from frontmatter. Returns null if we can't synthesize
// a meaningful one. Supports HowTo (best for build/automate/recipe pages),
// SoftwareApplication, Article (best for make/podcast pages).
function buildJsonLd(fm) {
  if (!fm || !fm.schemaType) return null;
  const base = {
    '@context': 'https://schema.org',
  };
  const type = String(fm.schemaType);

  if (type === 'HowTo') {
    // Parse "Step by step" out of the body would be ideal, but we don't have
    // it here. Use a minimal HowTo with name + description + the FAQ as
    // supplemental information.
    return {
      ...base,
      '@type': 'HowTo',
      name: fm.title || '',
      description: fm.metaDescription || fm.ogDescription || '',
      datePublished: fm.publishedAt || '',
      dateModified: fm.updatedAt || fm.publishedAt || '',
      author: { '@type': 'Organization', name: 'mChatAI', url: 'https://mchatai.com' },
    };
  }
  if (type === 'SoftwareApplication') {
    return {
      ...base,
      '@type': 'SoftwareApplication',
      name: fm.title || '',
      description: fm.metaDescription || '',
      applicationCategory: 'Productivity',
      operatingSystem: 'macOS',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
      url: fm.canonical || 'https://mchatai.com',
    };
  }
  if (type === 'Product') {
    return {
      ...base,
      '@type': 'Product',
      name: fm.title || '',
      description: fm.metaDescription || '',
      brand: { '@type': 'Brand', name: 'mChatAI' },
      url: fm.canonical || 'https://mchatai.com',
    };
  }
  return {
    ...base,
    '@type': 'Article',
    headline: fm.title || '',
    description: fm.metaDescription || '',
    datePublished: fm.publishedAt || '',
    dateModified: fm.updatedAt || fm.publishedAt || '',
    author: { '@type': 'Organization', name: 'mChatAI', url: 'https://mchatai.com' },
  };
}

export default SeoLandingPage;
